import type { PutWorkspaceSpecification } from '@festina/configuration-general-types/configuration'
import {
  type AllConciseWorkspaces,
  type LayerKey,
  useCreateOrUpdateConciseWorkspace,
  useDeleteConciseWorkspace,
  useFetchAllConciseWorkspaces,
  useRemoveWorkspaceOperation,
  type WorkspaceKey,
} from '@festina-life/api'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { useConfigurationComponentAvailability } from '../../hooks/useConfigurationComponentAvailability'
import { useAvailableServersForConfiguration } from '../../system/useAvailableServersForConfiguration'
import { fetchAllWorkspaceBranchChangesetsKey } from '../api/keys'
import { useAddOrUpdateWorkspaceOperationWithInvalidation } from '../api/templateCreateOperations/useAddOrUpdateWorkspaceOperationWithInvalidation'
import type { ChangeOperation, WorkspaceCreateOrUpdateInput } from './types'

export const useConfigurationComponentWorkspaces = () => {
  const isConfigurationComponentAvailable =
    useConfigurationComponentAvailability()
  const allServers = useAvailableServersForConfiguration()
  const { data: allConciseWorkspaces } = useFetchAllConciseWorkspaces({
    enabled: isConfigurationComponentAvailable,
    servers: allServers,
  })
  const { mutateAsync: addOrUpdateWorkspace } =
    useCreateOrUpdateConciseWorkspace([...allServers])
  const { mutateAsync: removeWorkspaceOperation } =
    useRemoveWorkspaceOperation()
  const addOrUpdateWorkspaceOperation =
    useAddOrUpdateWorkspaceOperationWithInvalidation()
  const { mutateAsync: removeWorkspace } = useDeleteConciseWorkspace()
  const emptyConciseWorkspaces: AllConciseWorkspaces = useMemo(
    () => Object.fromEntries(allServers.map((server) => [server, []])),
    [allServers],
  )
  const queryClient = useQueryClient()

  const handleAddWorkspace = useCallback(
    async (
      input: WorkspaceCreateOrUpdateInput,
      shouldUpdateCache?: boolean,
    ) => {
      const specification = {
        _type: 'PutWorkspaceSpecification',
        baseBranchName: input.branchKey.name,
      } satisfies PutWorkspaceSpecification

      await addOrUpdateWorkspace({
        server: input.branchKey.server,
        name: input.name,
        specification,
        shouldUpdateCache,
      })
    },
    [addOrUpdateWorkspace],
  )

  const handleUpdateWorkspace = useCallback(
    async (
      input: WorkspaceCreateOrUpdateInput,
      shouldUpdateCache?: boolean,
    ) => {
      const specification = {
        _type: 'PutWorkspaceSpecification',
        baseBranchName: input.branchKey.name,
      } satisfies PutWorkspaceSpecification

      await addOrUpdateWorkspace({
        server: input.branchKey.server,
        name: input.name,
        specification,
        shouldUpdateCache,
      })
    },
    [addOrUpdateWorkspace],
  )

  const remove = useCallback(
    async (key: WorkspaceKey) => {
      await removeWorkspace({ server: key.branchKey.server, name: key.name })

      await queryClient.invalidateQueries({
        queryKey: fetchAllWorkspaceBranchChangesetsKey({
          workspaceName: key.name,
          componentName: key.branchKey.server,
          baseBranchName: key.branchKey.name,
        }),
      })
    },
    [queryClient, removeWorkspace],
  )

  const handleAddOrUpdateOperation = useCallback(
    async ({
      operation,
      layer,
      workspaceKey,
      block,
    }: {
      operation: ChangeOperation
      layer: LayerKey
      workspaceKey: WorkspaceKey
      block: string
    }) => {
      await addOrUpdateWorkspaceOperation({
        block,
        operation,
        layer,
        name: workspaceKey.name,
        server: workspaceKey.branchKey.server,
        branchName: workspaceKey.branchKey.name,
      })
    },
    [addOrUpdateWorkspaceOperation],
  )

  const handleClearOperations = useCallback(() => {
    // There's currently no way to clear all workspace operations other than pushing the operations to a branch.
    throw new Error(
      'Unimplemented: Clearing workspace operations is not available for configuration component backed workspaces.',
    )
  }, [])

  const handleRemoveOperation = useCallback(
    (operationKey: string, key: WorkspaceKey) =>
      removeWorkspaceOperation({
        operationKey,
        workspaceName: key.name,
        server: key.branchKey.server,
      }),
    [removeWorkspaceOperation],
  )

  return {
    conciseWorkspaces: allConciseWorkspaces || emptyConciseWorkspaces,
    add: handleAddWorkspace,
    remove,
    update: handleUpdateWorkspace,
    addOrUpdateOperation: handleAddOrUpdateOperation,
    removeOperation: handleRemoveOperation,
    clearOperations: handleClearOperations,
  }
}
